/**
 * Builds the runtime Zod schema and the renderable field list for one entity's
 * enrichment object (design C.3, D.1).
 *
 * Two halves, one source of truth:
 *
 * - {@link resolveEnrichmentFields} says WHAT to render (key, label, control);
 * - {@link buildEnrichmentSchema} says what survives validation, attaching the
 *   same control to AutoForm as `fieldConfig({ fieldType })` metadata.
 *
 * The schema is `.passthrough()` throughout, on purpose. `PUT /{x}/enrich` is a
 * full replace onto a Firestore document with no version history, so any key
 * Zod or AutoForm drops is destroyed permanently — which is how `taskName` and
 * `translations` were being wiped on every curator approval.
 */
import { fieldConfig } from "@autoform/zod";
import { z } from "zod";

import { zGenreRef, zTransliterations } from "@/lib/zod/show-fragments";

import {
  type EnrichmentControl,
  type EnrichmentEntityKey,
  type EnrichmentPageConfig,
  labelOf,
} from "./config";
import {
  NEVER_INLINE_KEYS,
  PIPELINE_OWNED_KEY_LIST,
  READ_ONLY_KEY_LIST,
  clearableKeysFor,
  controlFor,
  deriveControl,
  isPlainObject,
} from "./field-rules";

/**
 * Control -> the AutoForm component name that renders it.
 *
 * `textarea`, `tagList`, `genres` and `hidden` are registered in
 * `injectCustomAutoFormComponents` today; `string`, `boolean`, `array` and
 * `object` are AutoForm's own. `features` and `json` are registered with the
 * editor in the component phase — until then AutoForm renders its
 * "no component found for type" notice, which is exactly the signal that the
 * registration is missing.
 */
export const AUTOFORM_FIELD_TYPE_BY_CONTROL: Record<EnrichmentControl, string> =
  {
    text: "string",
    textarea: "textarea",
    area: "enrichmentArea",
    stringList: "tagList",
    faq: "array",
    features: "features",
    genreRefs: "genres",
    boolean: "boolean",
    object: "object",
    json: "json",
    hidden: "hidden",
  };

/** One field, resolved for rendering. */
export interface ResolvedEnrichmentField {
  key: string;
  label: string;
  control: EnrichmentControl;
  /** The AutoForm component name for `control`. */
  fieldType: string;
  /** Informational: drives the "*" marker only. */
  required: boolean;
  /** May be sent as `""` when the curator empties it. */
  clearable: boolean;
  /** Rendered read-only (`json`) — the escape hatch for an unmodelled shape. */
  readOnly: boolean;
  /** The stored value is a number; the input wants a numeric type. */
  numeric: boolean;
  featureLabels?: Record<string, string>;
}

/** `{ en: [], el: [], ar: [], ro: [] }` — all four keys, per design C.5. */
export function emptyTransliterations(): Record<string, string[]> {
  return { en: [], el: [], ar: [], ro: [] };
}

/**
 * The four transliteration keys, forced present.
 *
 * `TransliterationsDbDto`'s members are `required` on seven of the eight
 * entities that have the object, so all four keys are always written, empty
 * arrays included. Missing members are normalised to `[]` rather than rejected:
 * a stored object that carries only `en` must not fail validation and block a
 * save.
 *
 * `.default([])`, NOT `.transform((value) => value ?? [])`. AutoForm walks every
 * nested member of the form schema, and `getFieldConfigInZodStack` reads
 * `effect.refinement` off each `ZodEffects` it meets — a bare `.transform()` has
 * none, so the read throws and the whole editor unmounts. `.default([])` fills
 * an absent member with the same `[]` and leaves no `ZodEffects` behind. A
 * stored `null` now survives as `null`; `normaliseTransliterations` in
 * `save-bodies.ts` already writes it back as `[]` on the save path.
 */
export const forcedTransliterationsSchema = zTransliterations.extend({
  en: z.array(z.string()).nullish().default([]),
  el: z.array(z.string()).nullish().default([]),
  ar: z.array(z.string()).nullish().default([]),
  ro: z.array(z.string()).nullish().default([]),
});

/** How deep an unmodelled nested object is recursed for its own controls. */
const MAX_OBJECT_DEPTH = 3;

/**
 * The Zod type a control validates with. Nullish everywhere: a stored value
 * that is absent or null must never block a save.
 *
 * `object` recurses, because AutoForm's ObjectField renders its children from
 * the sub-schema and nothing else would give those children a control.
 */
function zodForControl(
  control: EnrichmentControl,
  key: string,
  value: unknown,
  depth = 0,
): z.ZodTypeAny {
  switch (control) {
    case "text":
    case "textarea":
    case "area":
      return z.string().nullish();
    case "stringList":
      return z.array(z.string()).nullish();
    case "boolean":
      return z.boolean().nullish();
    case "faq":
      // Members are nullish, not required: a half-filled row is dropped by the
      // patch builder, and rejecting it here would block the whole save.
      return z
        .array(
          z
            .object({
              question: z.string().nullish(),
              answer: z.string().nullish(),
            })
            .passthrough(),
        )
        .nullish();
    case "genreRefs":
      return z.array(zGenreRef).nullish();
    case "features":
      return z.record(z.string(), z.unknown()).nullish();
    case "object": {
      if (!isPlainObject(value) || depth >= MAX_OBJECT_DEPTH) {
        return z.record(z.string(), z.unknown()).nullish();
      }
      const shape: Record<string, z.ZodTypeAny> = {};
      for (const [childKey, childValue] of Object.entries(value)) {
        const childControl = controlFor(childKey, childValue);
        if (childControl === "hidden") continue;
        shape[childKey] = withFieldType(
          zodForControl(childControl, childKey, childValue, depth + 1),
          childControl,
          labelOf({ key: childKey }),
        );
      }
      return z.object(shape).passthrough().nullish();
    }
    case "json":
    case "hidden":
    default:
      return z.unknown().nullish();
  }
}

/** Attach the AutoForm control metadata to a field's schema. */
function withFieldType(
  schema: z.ZodTypeAny,
  control: EnrichmentControl,
  label: string,
): z.ZodTypeAny {
  return schema.superRefine(
    fieldConfig({ fieldType: AUTOFORM_FIELD_TYPE_BY_CONTROL[control], label }),
  );
}

/**
 * What the editor renders, in order: the config's own fields first, then any
 * key the loaded object carries that the config does not name.
 *
 * A key with NO value and NO rule is skipped rather than thrown on — an absent
 * optional member (`socialLinks: null`) is a normal payload, not a modelling
 * failure. `controlFor` still throws if it is called directly on such a value.
 */
export function resolveEnrichmentFields(
  config: EnrichmentPageConfig,
  enrichment: Record<string, unknown> = {},
): ResolvedEnrichmentField[] {
  const fields: ResolvedEnrichmentField[] = [];
  const seen = new Set<string>();

  for (const rule of config.fields) {
    if (NEVER_INLINE_KEYS.has(rule.key)) continue;
    const value = enrichment[rule.key];
    const control = controlFor(rule.key, value, rule);
    if (control === "hidden") continue;
    seen.add(rule.key);
    fields.push({
      key: rule.key,
      label: labelOf(rule),
      control,
      fieldType: AUTOFORM_FIELD_TYPE_BY_CONTROL[control],
      required: rule.required ?? false,
      clearable:
        rule.clearable ?? (control === "text" || control === "textarea"),
      readOnly: control === "json",
      numeric: typeof value === "number",
      featureLabels: rule.featureLabels,
    });
  }

  for (const [key, value] of Object.entries(enrichment)) {
    if (seen.has(key)) continue;
    if (NEVER_INLINE_KEYS.has(key)) continue;
    if (value === null || value === undefined) continue;
    const control = controlFor(key, value);
    if (control === "hidden") continue;
    fields.push({
      key,
      label: labelFor(key),
      control,
      fieldType: AUTOFORM_FIELD_TYPE_BY_CONTROL[control],
      required: false,
      clearable: control === "text" || control === "textarea",
      readOnly: control === "json",
      numeric: typeof value === "number",
    });
  }

  return fields;
}

/** Local alias so the derived branch does not import the whole config module. */
function labelFor(key: string): string {
  return labelOf({ key });
}

/**
 * The runtime Zod schema for one entity's enrichment object.
 *
 * Shape = the resolved fields + the carried-through keys:
 * `transliterations` (forced to its four members) and `translations` when the
 * entity has them, plus every pipeline-owned or read-only key seen on the
 * loaded object, each as a `hidden` field, so AutoForm registers and returns
 * them instead of letting `removeEmptyValues` swallow them.
 */
export function buildEnrichmentSchema(
  config: EnrichmentPageConfig,
  enrichment: Record<string, unknown> = {},
): z.ZodObject<Record<string, z.ZodTypeAny>> {
  const shape: Record<string, z.ZodTypeAny> = {};

  for (const field of resolveEnrichmentFields(config, enrichment)) {
    shape[field.key] = withFieldType(
      zodForControl(field.control, field.key, enrichment[field.key]),
      field.control,
      field.label,
    );
  }

  if (config.hasTransliterations) {
    shape.transliterations = withFieldType(
      z.preprocess(
        (value) => (isPlainObject(value) ? value : {}),
        forcedTransliterationsSchema,
      ),
      "hidden",
      "Transliterations",
    );
  }

  shape.translations = withFieldType(
    z.record(z.string(), z.unknown()).nullish(),
    "hidden",
    "Translations",
  );

  for (const key of [...READ_ONLY_KEY_LIST, ...PIPELINE_OWNED_KEY_LIST]) {
    if (key in shape) continue;
    if (!(key in enrichment)) continue;
    shape[key] = withFieldType(z.unknown().nullish(), "hidden", labelFor(key));
  }

  return z.object(shape).passthrough();
}

/** Everything the editor and the save flow need for one entity. */
export interface EnrichmentFormBundle {
  config: EnrichmentPageConfig;
  fields: ResolvedEnrichmentField[];
  schema: z.ZodObject<Record<string, z.ZodTypeAny>>;
  /** The keys `withClearedFields` may emit `""` for. */
  clearableKeys: string[];
}

export function buildEnrichmentForm(
  config: EnrichmentPageConfig,
  enrichment: Record<string, unknown> = {},
): EnrichmentFormBundle {
  return {
    config,
    fields: resolveEnrichmentFields(config, enrichment),
    schema: buildEnrichmentSchema(config, enrichment),
    clearableKeys: clearableKeysFor(config),
  };
}

/** Re-exported for the tests and the page: a resource -> a renderable control. */
export { deriveControl, controlFor };

/** Convenience for callers that only have a resource name. */
export type { EnrichmentEntityKey };
